import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, { Background, Controls, MiniMap, Handle, Position, useNodesState, useEdgesState, Panel, getNodesBounds, getViewportForBounds } from "reactflow";
import "reactflow/dist/style.css";
import dagre from "dagre";
import { toPng } from "html-to-image";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  Network, Users, GitBranch, AlertCircle, Move, Plus, Download, Upload, Wand2, Building2, Table2, ImageDown,
} from "lucide-react";
import { toast } from "sonner";

const NODE_W = 240, NODE_H = 128;

function layout(positions) {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", nodesep: 40, ranksep: 70 });
  g.setDefaultEdgeLabel(() => ({}));
  positions.forEach((p) => g.setNode(p.id, { width: NODE_W, height: NODE_H }));
  positions.forEach((p) => { if (p.parent_position_id) g.setEdge(p.parent_position_id, p.id); });
  dagre.layout(g);
  return positions.map((p) => {
    const n = g.node(p.id) || { x: 0, y: 0 };
    return { id: p.id, x: n.x - NODE_W / 2, y: n.y - NODE_H / 2 };
  });
}

function initials(name = "") {
  return name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function PositionNode({ data }) {
  const p = data.position;
  return (
    <div data-testid="org-chart-node-position-card"
      className={cn("w-[240px] rounded-2xl border-2 bg-white p-3 shadow-sm transition-all hover:shadow-lg",
        p.is_vacant ? "border-rose-300 bg-rose-50/40" : p.has_plt ? "border-amber-300" : "border-slate-200 hover:border-indigo-400")}>
      <Handle type="target" position={Position.Top} />
      <div className="flex items-center gap-2.5">
        <div className={cn("flex h-10 w-10 items-center justify-center rounded-full text-xs font-bold text-white shrink-0",
          p.is_vacant ? "bg-rose-400" : "bg-gradient-to-br from-indigo-500 to-purple-500")}>
          {p.holder ? initials(p.holder.name) : "?"}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-800 truncate leading-tight">{p.holder?.name || "Posisi Kosong"}</p>
          <p className="text-[11px] text-indigo-600 font-semibold truncate">{p.title}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {p.branch && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">{p.branch.name}</span>}
        {p.division && <span className="text-[10px] px-1.5 py-0.5 rounded font-medium text-white" style={{ background: p.division.color }}>{p.division.code}</span>}
        {p.subordinate_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold inline-flex items-center gap-0.5"><Users className="h-2.5 w-2.5" />{p.subordinate_count}</span>}
      </div>
      <div className="flex items-center gap-1.5 mt-1.5">
        {p.is_vacant && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-bold inline-flex items-center gap-0.5"><AlertCircle className="h-2.5 w-2.5" />VACANT</span>}
        {p.has_plt && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">Plt: {p.plt_user?.name?.split(" ")[0]}</span>}
        {p.rangkap_users?.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-bold">Rangkap</span>}
        {p.dotted_parents?.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 font-bold inline-flex items-center gap-0.5"><GitBranch className="h-2.5 w-2.5" />Dotted</span>}
      </div>
      {data.isAdmin && (
        <button onClick={() => data.onMove(p)} data-testid={`move-position-${p.id}`}
          className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-white shadow hover:bg-indigo-700">
          <Move className="h-3 w-3" />
        </button>
      )}
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

const nodeTypes = { position: PositionNode };

function OrgChart({ positions, isAdmin, onReload }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const [moveTarget, setMoveTarget] = useState(null);

  const descendants = useCallback((pid) => {
    const set = new Set([pid]);
    let added = true;
    while (added) {
      added = false;
      positions.forEach((p) => { if (p.parent_position_id && set.has(p.parent_position_id) && !set.has(p.id)) { set.add(p.id); added = true; } });
    }
    return set;
  }, [positions]);

  const doMove = async (pid, newParent) => {
    try { await api.post(`/org/positions/${pid}/move`, { parent_position_id: newParent, reason: "Drag-and-drop di editor bagan" }); toast.success("Posisi dipindahkan"); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal memindahkan"); onReload(); }
  };

  useEffect(() => {
    const pos = layout(positions);
    const posMap = Object.fromEntries(pos.map((p) => [p.id, p]));
    setNodes(positions.map((p) => ({
      id: p.id, type: "position", position: { x: posMap[p.id].x, y: posMap[p.id].y },
      data: { position: p, isAdmin, onMove: (pp) => setMoveTarget(pp) }, draggable: isAdmin,
    })));
    const solid = positions.filter((p) => p.parent_position_id).map((p) => ({
      id: `e-${p.parent_position_id}-${p.id}`, source: p.parent_position_id, target: p.id,
      type: "smoothstep", style: { stroke: "#94a3b8", strokeWidth: 2 },
    }));
    const dotted = [];
    positions.forEach((p) => (p.dotted_parent_ids || []).forEach((dp) => {
      dotted.push({ id: `d-${dp}-${p.id}`, source: dp, target: p.id, type: "smoothstep", animated: true,
        style: { stroke: "#d946ef", strokeWidth: 1.5, strokeDasharray: "5 5" }, label: "dotted" });
    }));
    setEdges([...solid, ...dotted]);
  }, [positions, isAdmin]);

  const onNodeDragStop = useCallback((evt, node) => {
    if (!isAdmin) return;
    const cx = node.position.x + NODE_W / 2, cy = node.position.y + NODE_H / 2;
    const desc = descendants(node.id);
    let target = null;
    nodes.forEach((n) => {
      if (n.id === node.id || desc.has(n.id)) return;
      if (cx >= n.position.x && cx <= n.position.x + NODE_W && cy >= n.position.y && cy <= n.position.y + NODE_H) target = n;
    });
    const current = positions.find((p) => p.id === node.id);
    if (target && target.id !== current?.parent_position_id) {
      const tp = positions.find((p) => p.id === target.id);
      if (window.confirm(`Pindahkan "${current.title}" menjadi bawahan "${tp.title}"?`)) { doMove(node.id, target.id); return; }
    }
    onReload();
  }, [nodes, positions, isAdmin]);

  const [exporting, setExporting] = useState(false);
  const exportImage = async () => {
    if (!nodes.length) return;
    setExporting(true);
    try {
      const bounds = getNodesBounds(nodes);
      const W = Math.max(Math.ceil(bounds.width) + 120, 800);
      const H = Math.max(Math.ceil(bounds.height) + 120, 600);
      const vp = getViewportForBounds(bounds, W, H, 0.2, 2, 0.08);
      const el = document.querySelector(".react-flow__viewport");
      const dataUrl = await toPng(el, {
        backgroundColor: "#f8fafc", width: W, height: H, skipFonts: true, cacheBust: true,
        style: { width: `${W}px`, height: `${H}px`, transform: `translate(${vp.x}px, ${vp.y}px) scale(${vp.zoom})` },
      });
      const a = document.createElement("a"); a.href = dataUrl; a.download = "bagan_organisasi_zoa.png"; a.click();
      toast.success("Bagan diekspor sebagai gambar");
    } catch (e) { toast.error("Gagal mengekspor bagan"); }
    setExporting(false);
  };

  return (
    <div className="h-[calc(100vh-260px)] min-h-[500px] rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} nodeTypes={nodeTypes}
        onNodeDragStop={onNodeDragStop} fitView minZoom={0.2} proOptions={{ hideAttribution: true }}>
        <Background color="#e2e8f0" gap={20} />
        <Controls />
        <MiniMap nodeColor={(n) => (n.data?.position?.is_vacant ? "#f87171" : "#818cf8")} pannable zoomable />
        <Panel position="top-right">
          <Button size="sm" variant="outline" onClick={exportImage} disabled={exporting} data-testid="export-chart-image-btn" className="bg-white shadow-md">
            <ImageDown className="h-4 w-4 mr-1.5" />{exporting ? "Mengekspor..." : "Ekspor Gambar"}
          </Button>
        </Panel>
      </ReactFlow>
      <MoveDialog target={moveTarget} positions={positions} descendants={descendants}
        onClose={() => setMoveTarget(null)} onMove={(np) => { doMove(moveTarget.id, np); setMoveTarget(null); }} />
    </div>
  );
}

function MoveDialog({ target, positions, descendants, onClose, onMove }) {
  const [parent, setParent] = useState("");
  useEffect(() => { setParent(target?.parent_position_id || ""); }, [target]);
  if (!target) return null;
  const desc = descendants(target.id);
  const options = positions.filter((p) => !desc.has(p.id));
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Ubah Atasan: {target.title}</DialogTitle></DialogHeader>
        <Label>Posisi Atasan (Solid Line)</Label>
        <Select value={parent} onValueChange={setParent}>
          <SelectTrigger data-testid="move-parent-select"><SelectValue placeholder="Pilih atasan" /></SelectTrigger>
          <SelectContent>{options.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={() => onMove(parent)} data-testid="move-confirm-btn" className="bg-indigo-600 hover:bg-indigo-700">Pindahkan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrgTable({ positions, isAdmin, onEdit, onReload }) {
  const [branch, setBranch] = useState("all");
  const [branches, setBranches] = useState([]);
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)); }, []);
  const filtered = positions.filter((p) => branch === "all" || p.branch_id === branch);
  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Select value={branch} onValueChange={setBranch}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Cabang</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr><th className="text-left px-4 py-2.5">Kode</th><th className="text-left px-3">Jabatan</th><th className="text-left px-3">Level</th><th className="text-left px-3">Divisi</th><th className="text-left px-3">Cabang</th><th className="text-left px-3">Pemegang</th><th className="text-left px-3">Status</th>{isAdmin && <th className="text-right px-4">Aksi</th>}</tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`org-table-row-${p.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{p.code}</td>
                <td className="px-3 font-medium text-slate-700">{p.title}</td>
                <td className="px-3 text-slate-500">{p.level?.name}</td>
                <td className="px-3">{p.division ? <span className="text-xs font-semibold" style={{ color: p.division.color }}>{p.division.name}</span> : "-"}</td>
                <td className="px-3 text-slate-500">{p.branch?.name}</td>
                <td className="px-3 text-slate-700">{p.holder?.name || <span className="text-rose-500 font-semibold">Kosong</span>}{p.holder_type === "plt" && <span className="ml-1 text-xs text-amber-600">(Plt)</span>}</td>
                <td className="px-3">{p.is_vacant ? <span className="text-xs text-rose-600 font-bold">VACANT</span> : p.has_plt ? <span className="text-xs text-amber-600 font-bold">PLT</span> : <span className="text-xs text-emerald-600">Aktif</span>}</td>
                {isAdmin && <td className="px-4 text-right"><Button size="sm" variant="ghost" onClick={() => onEdit(p)} data-testid={`org-edit-${p.id}`}>Edit</Button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && <p className="text-center text-slate-400 py-10" data-testid="org-table-empty">Belum ada posisi.</p>}
    </div>
  );
}

function Simulator() {
  const [users, setUsers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [userId, setUserId] = useState("");
  const [docType, setDocType] = useState("pengajuan");
  const [budget, setBudget] = useState("");
  const [divs, setDivs] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/org/users").then((r) => setUsers(r.data));
    api.get("/divisions").then((r) => setDivisions(r.data));
  }, []);

  const run = async () => {
    if (!userId) return toast.error("Pilih user");
    try {
      const { data } = await api.post("/org/simulate-approval", {
        user_id: userId, document_type: docType, budget: Number(budget || 0), divisions_involved: divs,
      });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simulasi"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <p className="font-bold text-slate-700 flex items-center gap-2"><Wand2 className="h-4 w-4 text-indigo-500" />Simulator Jalur Persetujuan</p>
        <div>
          <Label>Pengaju (User)</Label>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="sim-user-select"><SelectValue placeholder="Pilih user" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Jenis Dokumen</Label>
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pengajuan">Pengajuan Rencana</SelectItem>
              <SelectItem value="misi_baru">Misi/Program Baru</SelectItem>
              <SelectItem value="perpanjangan">Perpanjangan Deadline</SelectItem>
              <SelectItem value="aktual_target">Aktual Target</SelectItem>
              <SelectItem value="laporan_bulanan">Laporan Bulanan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Nilai Anggaran (Rp)</Label><Input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="0" data-testid="sim-budget-input" /></div>
        <div>
          <Label className="mb-1.5 block">Divisi Terlibat</Label>
          <div className="grid grid-cols-2 gap-1.5">
            {divisions.map((d) => (
              <label key={d.id} className="flex items-center gap-2 text-sm">
                <Checkbox checked={divs.includes(d.id)} onCheckedChange={() => setDivs((s) => s.includes(d.id) ? s.filter((x) => x !== d.id) : [...s, d.id])} />{d.name}
              </label>
            ))}
          </div>
        </div>
        <Button onClick={run} data-testid="sim-run-btn" className="w-full bg-indigo-600 hover:bg-indigo-700">Simulasikan</Button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="font-bold text-slate-700 mb-4">Hasil Rantai Persetujuan</p>
        {!result ? <p className="text-sm text-slate-400 italic">Jalankan simulasi untuk melihat rantai approval.</p> : (
          <div className="space-y-3" data-testid="sim-result">
            <div className="text-sm"><span className="text-slate-400">Pengaju:</span> <span className="font-semibold">{result.requester}</span> ({result.start_position})</div>
            {result.sequential_chain.map((s) => (
              <div key={s.level} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white text-sm font-bold shrink-0">{s.level}</div>
                <div className="flex-1"><p className="text-sm font-semibold text-slate-700">{s.position_title}</p><p className="text-xs text-slate-400">{s.role} · {s.holder}</p></div>
              </div>
            ))}
            {result.parallel_approvers?.length > 0 && (
              <div className="pt-2">
                <p className="text-xs font-semibold text-fuchsia-600 uppercase mb-2">Approver Paralel</p>
                {result.parallel_approvers.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-xl border border-fuchsia-200 bg-fuchsia-50/50 p-2.5 mb-1.5">
                    <GitBranch className="h-4 w-4 text-fuchsia-500" />
                    <div><p className="text-sm font-medium text-slate-700">{p.position_title}</p><p className="text-xs text-slate-400">{p.role} · {p.holder}</p></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ImportExport({ onReload }) {
  const [csv, setCsv] = useState("");
  const exportCsv = async () => {
    const res = await api.get("/org/export", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = url; a.download = "struktur_zoa.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const doImport = async () => {
    if (!csv.trim()) return toast.error("Tempel isi CSV dulu");
    try { const r = await api.post("/org/import", { csv_text: csv }); toast.success(`Impor selesai (${r.data.imported} baris)`); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal impor"); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="font-bold text-slate-700 flex items-center gap-2 mb-2"><Download className="h-4 w-4 text-indigo-500" />Ekspor Struktur</p>
        <p className="text-sm text-slate-500 mb-4">Unduh seluruh posisi dalam format CSV (kode, jabatan, level, divisi, cabang, kode atasan, dotted).</p>
        <Button onClick={exportCsv} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-1.5" />Unduh CSV</Button>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="font-bold text-slate-700 flex items-center gap-2 mb-2"><Upload className="h-4 w-4 text-indigo-500" />Impor Struktur</p>
        <p className="text-sm text-slate-500 mb-2">Tempel isi CSV (header: code,title,level_id,division_id,branch_id,parent_code,dotted_parent_codes,headcount).</p>
        <Textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={6} placeholder="code,title,level_id,..." className="font-mono text-xs mb-3" data-testid="import-textarea" />
        <Button onClick={doImport} data-testid="import-btn" className="bg-indigo-600 hover:bg-indigo-700"><Upload className="h-4 w-4 mr-1.5" />Impor</Button>
      </div>
    </div>
  );
}

export default function OrgStructure() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "owner"].includes(user?.role);
  const [positions, setPositions] = useState([]);
  const [levels, setLevels] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [posDialog, setPosDialog] = useState({ open: false, edit: null });
  const load = () => api.get("/org/positions").then((r) => setPositions(r.data));
  useEffect(() => {
    load();
    api.get("/org/levels").then((r) => setLevels(r.data));
    api.get("/divisions").then((r) => setDivisions(r.data));
    api.get("/branches").then((r) => setBranches(r.data));
  }, []);

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Network className="h-7 w-7 text-indigo-500" />Struktur Organisasi</h1>
          <p className="text-slate-500 text-sm">Bagan berbasis posisi · {isAdmin ? "Seret kartu ke atasan baru untuk memindahkan (drag-and-drop)" : "Tampilan hanya-baca"}</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setPosDialog({ open: true, edit: null })} data-testid="add-position-btn" className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-1.5" />Tambah Posisi
          </Button>
        )}
      </div>

      <Tabs defaultValue="bagan">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="bagan" data-testid="org-tab-bagan"><Network className="h-4 w-4 mr-1.5" />Bagan</TabsTrigger>
          <TabsTrigger value="tabel" data-testid="org-tab-tabel"><Table2 className="h-4 w-4 mr-1.5" />Tabel</TabsTrigger>
          <TabsTrigger value="simulator" data-testid="org-tab-simulator"><Wand2 className="h-4 w-4 mr-1.5" />Simulator</TabsTrigger>
          <TabsTrigger value="import" data-testid="org-tab-import"><Upload className="h-4 w-4 mr-1.5" />Impor/Ekspor</TabsTrigger>
        </TabsList>
        <TabsContent value="bagan">
          {positions.length > 0
            ? <OrgChart positions={positions} isAdmin={isAdmin} onReload={load} />
            : <EmptyOrg isAdmin={isAdmin} onAdd={() => setPosDialog({ open: true, edit: null })} />}
        </TabsContent>
        <TabsContent value="tabel"><OrgTable positions={positions} isAdmin={isAdmin} onEdit={(p) => setPosDialog({ open: true, edit: p })} onReload={load} /></TabsContent>
        <TabsContent value="simulator"><Simulator /></TabsContent>
        <TabsContent value="import"><ImportExport onReload={load} /></TabsContent>
      </Tabs>

      <PositionDialog
        open={posDialog.open}
        edit={posDialog.edit}
        positions={positions}
        levels={levels}
        divisions={divisions}
        branches={branches}
        onClose={() => setPosDialog({ open: false, edit: null })}
        onSaved={() => { setPosDialog({ open: false, edit: null }); load(); }}
      />
    </div>
  );
}

function EmptyOrg({ isAdmin, onAdd }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white py-20 text-center" data-testid="org-empty">
      <Network className="h-10 w-10 text-slate-300 mx-auto mb-3" />
      <p className="font-semibold text-slate-600">Struktur organisasi masih kosong</p>
      <p className="text-sm text-slate-400 mb-4">Buat posisi pertama (mis. CEO) sebagai akar, lalu tambahkan jabatan di bawahnya.</p>
      {isAdmin && <Button onClick={onAdd} data-testid="add-first-position-btn" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1.5" />Tambah Posisi Pertama</Button>}
    </div>
  );
}

const EMPTY_POS = { code: "", title: "", level_id: "", division_id: "", branch_id: "", parent_position_id: "", headcount: 1, description: "" };

function PositionDialog({ open, edit, positions, levels, divisions, branches, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY_POS);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (edit) setF({ code: edit.code || "", title: edit.title || "", level_id: edit.level_id || "", division_id: edit.division_id || "", branch_id: edit.branch_id || "", parent_position_id: edit.parent_position_id || "", headcount: edit.headcount || 1, description: edit.description || "" });
    else setF(EMPTY_POS);
  }, [open, edit]);

  const save = async () => {
    if (!f.code.trim() || !f.title.trim() || !f.level_id) return toast.error("Kode, jabatan & level wajib diisi");
    setBusy(true);
    try {
      const payload = { ...f, division_id: f.division_id || null, branch_id: f.branch_id || null, parent_position_id: f.parent_position_id || null, headcount: Number(f.headcount) || 1 };
      if (edit) await api.put(`/org/positions/${edit.id}`, payload);
      else await api.post("/org/positions", payload);
      toast.success(edit ? "Posisi diperbarui" : "Posisi dibuat");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    setBusy(false);
  };

  const del = async () => {
    if (!window.confirm(`Arsipkan posisi "${edit?.title}"?`)) return;
    try { await api.delete(`/org/positions/${edit.id}`); toast.success("Posisi diarsipkan"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const parentOptions = positions.filter((p) => !edit || p.id !== edit.id);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto zoa-scroll" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>{edit ? "Edit Posisi" : "Tambah Posisi"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Kode</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="mis. CEO" data-testid="pos-code-input" /></div>
            <div className="space-y-1.5"><Label>Jabatan</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Chief Executive Officer" data-testid="pos-title-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Level</Label>
              <Select value={f.level_id} onValueChange={(v) => setF({ ...f, level_id: v })}>
                <SelectTrigger data-testid="pos-level-select"><SelectValue placeholder="Pilih level" /></SelectTrigger>
                <SelectContent>{levels.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} (Rank {l.rank})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Headcount</Label><Input type="number" value={f.headcount} onChange={(e) => setF({ ...f, headcount: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Divisi</Label>
              <Select value={f.division_id || "none"} onValueChange={(v) => setF({ ...f, division_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{divisions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Cabang</Label>
              <Select value={f.branch_id || "none"} onValueChange={(v) => setF({ ...f, branch_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Atasan (Posisi Induk)</Label>
            <Select value={f.parent_position_id || "none"} onValueChange={(v) => setF({ ...f, parent_position_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="pos-parent-select"><SelectValue placeholder="— akar (tanpa atasan) —" /></SelectTrigger>
              <SelectContent><SelectItem value="none">— akar (tanpa atasan) —</SelectItem>{parentOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.title} ({p.code})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Deskripsi</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="opsional" /></div>
        </div>
        <DialogFooter>
          {edit && <Button variant="outline" onClick={del} className="mr-auto text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="pos-archive-btn">Arsipkan</Button>}
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="pos-save-btn">{busy ? "Menyimpan…" : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
